import { useState, useRef } from 'react'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { authHeaders } from '../Comisiones'
import styles from './Components.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

const ESTADO_CONFIG = {
    borrador:  { label: 'Borrador',   color: '#63b3ed', bg: 'rgba(99,179,237,0.15)' },
    enviado:   { label: 'Pendiente',  color: '#ffb800', bg: 'rgba(255,184,0,0.15)' },
    aprobado:  { label: 'Aprobado',   color: '#48c78e', bg: 'rgba(72,199,142,0.15)' },
    rechazado: { label: 'Rechazado',  color: '#f87171', bg: 'rgba(248,113,113,0.15)' },
}

const normalizeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()

const money = (value) => {
    if (typeof value === 'number') return value
    const clean = String(value || '').replace(/[$,%\s]/g, '').replace(/^-$/, '')
    return Number(clean) || 0
}

const percent = (value) => {
    if (typeof value === 'string' && value.includes('%')) return money(value)
    const n = money(value)
    return n <= 1 ? n * 100 : n
}

const readWorkbook = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (ev) => {
        try {
            resolve(XLSX.read(ev.target.result, { type: 'array', cellDates: true }))
        } catch (err) {
            reject(err)
        }
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
})

const findSection = (rows, label) =>
    rows.findIndex(row => normalizeText(row?.[0]).includes(label))

const readSectionRows = (rows, sectionIndex) => {
    const data = []
    for (let i = sectionIndex + 2; i < rows.length; i++) {
        const name = String(rows[i]?.[0] || '').trim()
        if (!name) continue
        if (normalizeText(name).startsWith('TOTAL GENERAL')) break
        data.push(rows[i])
    }
    return data
}

const findPlanByVendor = (planes, vendor) => {
    const vendorKey = normalizeText(vendor)
    const vendorTokens = new Set(vendorKey.split(' ').filter(Boolean))
    let best = null
    let bestScore = 0

    for (const plan of planes) {
        const planKey = normalizeText(plan.gestor_nombre || plan.gestor_email)
        if (planKey === vendorKey) return plan

        const planTokens = new Set(planKey.split(' ').filter(Boolean))
        const intersection = [...vendorTokens].filter(token => planTokens.has(token)).length
        const score = intersection / Math.max(vendorTokens.size, planTokens.size, 1)
        if (score > bestScore) {
            best = plan
            bestScore = score
        }
    }

    return bestScore >= 0.65 ? best : null
}

const comisionPctPorCumplimiento = (pct) => {
    if (pct < 30) return 0
    if (pct < 50) return 0.3
    if (pct < 70) return 0.5
    if (pct < 100) return 0.7
    return 1
}

const getSheetRows = (wb, sheetName) => {
    const ws = wb.Sheets[sheetName]
    return ws ? XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' }) : null
}

const headerIndex = (headers, label) =>
    headers.findIndex(h => normalizeText(h) === normalizeText(label))

const pvpBand = (unitario, row, idx) => {
    const precios = [1, 2, 3, 4, 5, 6].map(n => money(row[idx[`pvp${n}`]]))
    const labels = ['pvp 1', 'pvp2', 'pvp3', 'pvp4', 'pvp5', 'pvp6']

    for (let i = 0; i < precios.length; i++) {
        if (precios[i] && unitario === precios[i]) return labels[i]
    }
    if (precios[0] && unitario >= precios[0]) return labels[0]
    if (precios[1] && unitario > precios[1]) return labels[0]
    if (precios[2] && unitario > precios[2]) return labels[1]
    if (precios[3] && unitario > precios[3]) return labels[2]
    if (precios[4] && unitario > precios[4]) return labels[3]
    return labels[4]
}

const parseVentasDetalle = (wb) => {
    const rowsMeta = getSheetRows(wb, 'META')
    const rowsBase = getSheetRows(wb, 'BASE')
    if (!rowsMeta || !rowsBase) return null

    const metaByName = new Map()
    for (let i = 1; i < rowsMeta.length; i++) {
        const name = rowsMeta[i]?.[0]
        const nameKey = normalizeText(name)
        if (!name || nameKey.startsWith('ETIQUETAS')) continue
        if (nameKey.startsWith('TOTAL')) break
        const meta = money(rowsMeta[i]?.[1])
        const ventas = money(rowsMeta[i]?.[2])
        if (meta || ventas) {
            metaByName.set(normalizeText(name), {
                nombre: name,
                meta_q1: meta,
                ventas_periodo: ventas,
                pct: meta > 0 ? (ventas / meta) * 100 : 0,
            })
        }
    }

    const headers = rowsBase[2] || []
    const idx = {
        vendedor: headerIndex(headers, 'Vendedor'),
        ventas: headerIndex(headers, 'Ventas'),
        unitario: headerIndex(headers, 'Unitario Venta'),
        pvp1: headerIndex(headers, 'pvp 1'),
        pvp2: headerIndex(headers, 'pvp2'),
        pvp3: headerIndex(headers, 'pvp3'),
        pvp4: headerIndex(headers, 'pvp4'),
        pvp5: headerIndex(headers, 'pvp5'),
        pvp6: headerIndex(headers, 'pvp6'),
        pvp: headerIndex(headers, 'pvp'),
        cumplimiento: headerIndex(headers, 'Cumplimiento'),
    }
    if (idx.vendedor < 0 || idx.ventas < 0 || idx.unitario < 0) return null

    const result = new Map()
    for (let i = 3; i < rowsBase.length; i++) {
        const row = rowsBase[i]
        const vendedor = row[idx.vendedor]
        if (!vendedor) continue
        const key = normalizeText(vendedor)
        const meta = metaByName.get(key) || { nombre: vendedor, pct: 0, ventas_periodo: 0, meta_q1: 0 }
        const ventas = money(row[idx.ventas])
        const unitario = money(row[idx.unitario])
        const pctComision = idx.cumplimiento >= 0
            ? percent(row[idx.cumplimiento])
            : comisionPctPorCumplimiento(meta.pct)
        const band = idx.pvp >= 0
            ? normalizeText(row[idx.pvp]).toLowerCase()
            : pvpBand(unitario, row, idx)
        const current = result.get(key) || {
            nombre: vendedor,
            ventas_periodo: meta.ventas_periodo,
            meta_q1: meta.meta_q1,
            comision_ventas_pvp_1_3_cop: 0,
            comision_ventas_pvp_4_6_cop: 0,
        }

        if (['pvp 1', 'pvp2', 'pvp3', 'pvp4'].includes(band)) {
            current.comision_ventas_pvp_1_3_cop += ventas * pctComision / 100
        } else {
            current.comision_ventas_pvp_4_6_cop += ventas * (pctComision / 2) / 100
        }
        result.set(key, current)
    }

    return result
}

const parseRecaudoDetalle = (wb, ventasByName = new Map()) => {
    const rows = getSheetRows(wb, 'COMISIONES') || getSheetRows(wb, 'BASE')
    if (!rows) return null

    const headers = rows[2] || []
    const idx = {
        recaudador: headerIndex(headers, 'Recaudador'),
        fechaFra: headerIndex(headers, 'Fecha Fra'),
        base: headerIndex(headers, 'BaseComision'),
        comisPct: headerIndex(headers, 'Comis %'),
    }
    if (idx.recaudador < 0 || idx.base < 0) return null

    const result = new Map()
    for (let i = 3; i < rows.length; i++) {
        const row = rows[i]
        const recaudador = row[idx.recaudador]
        if (!recaudador) continue
        const key = normalizeText(recaudador)
        const venta = ventasByName.get(key)
        const pctRecaudo = idx.comisPct >= 0
            ? percent(row[idx.comisPct])
            : comisionPctPorCumplimiento(venta?.pct || 0) / 2
        const base = money(row[idx.base])
        const fecha = row[idx.fechaFra]
        const year = fecha instanceof Date ? fecha.getFullYear() : Number(String(fecha || '').match(/\b(20\d{2}|\d{2})\b/)?.[1])
        const current = result.get(key) || {
            nombre: recaudador,
            recaudo_periodo: 0,
            comision_recaudo_q4_cop: 0,
            comision_recaudo_q1_cop: 0,
        }
        current.recaudo_periodo += base
        if (year === 2025 || year === 25) current.comision_recaudo_q4_cop += base * pctRecaudo / 100
        else current.comision_recaudo_q1_cop += base * pctRecaudo / 100
        result.set(key, current)
    }

    return result
}

const buildLiquidacionFromInsumos = (workbooks, planes) => {
    let ventasByName = null
    let recaudoByName = null

    for (const wb of workbooks) {
        if (!ventasByName) ventasByName = parseVentasDetalle(wb)
    }
    for (const wb of workbooks) {
        const parsed = parseRecaudoDetalle(wb, ventasByName || new Map())
        if (parsed) {
            recaudoByName = parsed
            break
        }
    }

    if (!ventasByName && !recaudoByName) return null
    const names = new Set([...(ventasByName?.keys() || []), ...(recaudoByName?.keys() || [])])
    const byPlan = new Map()
    for (const name of names) {
        const venta = ventasByName?.get(name) || {}
        const recaudo = recaudoByName?.get(name) || {}
        const plan = findPlanByVendor(planes, venta.nombre || recaudo.nombre)
        const key = plan?.gestor_email || name
        const current = byPlan.get(key) || {
            plan,
            payload: {
                ventas_periodo: 0,
                recaudo_periodo: 0,
                nuevos_clientes: 0,
                nuevos_comodatos: 0,
                notas: 'Calculado desde insumos de ventas y recaudo',
                estado: 'borrador',
                comision_ventas_pvp_1_3_cop: 0,
                comision_ventas_pvp_4_6_cop: 0,
                comision_recaudo_q4_cop: 0,
                comision_recaudo_q1_cop: 0,
                liquidacion_fuente: 'Insumos portal',
            },
            nombre: venta.nombre || recaudo.nombre,
        }
        current.plan = current.plan || plan
        current.payload.ventas_periodo += venta.ventas_periodo || 0
        current.payload.recaudo_periodo += recaudo.recaudo_periodo || 0
        current.payload.comision_ventas_pvp_1_3_cop += venta.comision_ventas_pvp_1_3_cop || 0
        current.payload.comision_ventas_pvp_4_6_cop += venta.comision_ventas_pvp_4_6_cop || 0
        current.payload.comision_recaudo_q4_cop += recaudo.comision_recaudo_q4_cop || 0
        current.payload.comision_recaudo_q1_cop += recaudo.comision_recaudo_q1_cop || 0
        byPlan.set(key, current)
    }

    return [...byPlan.values()].map(row => ({
        ...row,
        payload: {
            ...row.payload,
            comision_ventas_pvp_1_3_cop: Math.round(row.payload.comision_ventas_pvp_1_3_cop * 100) / 100,
            comision_ventas_pvp_4_6_cop: Math.round(row.payload.comision_ventas_pvp_4_6_cop * 100) / 100,
            comision_recaudo_q4_cop: Math.round(row.payload.comision_recaudo_q4_cop * 100) / 100,
            comision_recaudo_q1_cop: Math.round(row.payload.comision_recaudo_q1_cop * 100) / 100,
        },
    }))
}

const parseConsolidadoQ1 = (wb, planes) => {
    const ws = wb.Sheets['TOTAL COMISIONES']
    if (!ws) return null

    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' })
    const ventasIdx = findSection(rows, 'COMISION TRIMESTRAL POR VENTAS')
    const recaudoIdx = findSection(rows, 'COMISION POR RECAUDO')
    if (ventasIdx < 0 || recaudoIdx < 0) return null

    const ventasByName = new Map()
    for (const row of readSectionRows(rows, ventasIdx)) {
        ventasByName.set(normalizeText(row[0]), {
            nombre: row[0],
            ventas_periodo: money(row[1]),
            meta_q1: money(row[2]),
            comision_ventas_pvp_1_3_cop: money(row[5]),
            comision_ventas_pvp_4_6_cop: money(row[6]),
        })
    }

    const recaudoByName = new Map()
    for (const row of readSectionRows(rows, recaudoIdx)) {
        recaudoByName.set(normalizeText(row[0]), {
            nombre: row[0],
            recaudo_periodo: money(row[1]),
            comision_recaudo_q4_cop: money(row[5]),
            comision_recaudo_q1_cop: money(row[6]),
        })
    }

    const names = new Set([...ventasByName.keys(), ...recaudoByName.keys()])
    return [...names].map(name => {
        const venta = ventasByName.get(name) || {}
        const recaudo = recaudoByName.get(name) || {}
        const plan = findPlanByVendor(planes, venta.nombre || recaudo.nombre)
        return {
            plan,
            payload: {
                ventas_periodo: venta.ventas_periodo || 0,
                recaudo_periodo: recaudo.recaudo_periodo || 0,
                nuevos_clientes: 0,
                nuevos_comodatos: 0,
                notas: 'Importado desde CONSOLIDADO Q1 2026',
                estado: 'borrador',
                comision_ventas_pvp_1_3_cop: venta.comision_ventas_pvp_1_3_cop || 0,
                comision_ventas_pvp_4_6_cop: venta.comision_ventas_pvp_4_6_cop || 0,
                comision_recaudo_q4_cop: recaudo.comision_recaudo_q4_cop || 0,
                comision_recaudo_q1_cop: recaudo.comision_recaudo_q1_cop || 0,
                liquidacion_fuente: 'CONSOLIDADO Q1 2026',
            },
            nombre: venta.nombre || recaudo.nombre,
        }
    })
}

// ── Plantilla Excel de importación ────────────────────────────────────────────
function generarPlantillaExcel() {
    const wb = XLSX.utils.book_new()
    const wsData = [
        ['PLANTILLA IMPORTACIÓN — Comisiones Ingconia'],
        ['Instrucciones: Complete los campos y no modifique los encabezados'],
        [],
        ['gestor_email', 'ventas_periodo', 'recaudo_periodo', 'nuevos_clientes', 'nuevos_comodatos', 'notas'],
        ['gestor@empresa.com', 1500000000, 1200000000, 3, 1, 'Comentario opcional'],
    ]
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 16 }, { wch: 18 }, { wch: 30 }]
    XLSX.utils.book_append_sheet(wb, ws, 'Datos')
    XLSX.writeFile(wb, 'plantilla_comisiones.xlsx')
}

// ── Modal para ingresar datos de un gestor ────────────────────────────────────
function DatosFormModal({ empresaId, periodo, plan, datos, onClose, onSave, setMsg, isAdmin }) {
    const [form, setForm] = useState({
        ventas_periodo:   datos?.ventas_periodo  || '',
        recaudo_periodo:  datos?.recaudo_periodo || '',
        nuevos_clientes:  datos?.nuevos_clientes || 0,
        nuevos_comodatos: datos?.nuevos_comodatos || 0,
        notas:            datos?.notas           || '',
        estado:           datos?.estado          || 'borrador',
    })
    const [saving, setSaving] = useState(false)

    // Calcular % cumplimiento en tiempo real
    const metaPeriodo = Number(plan?.metas?.periodo || plan?.metas?.q1 || 0)
    const pctVenta = metaPeriodo > 0 ? ((Number(form.ventas_periodo) / metaPeriodo) * 100) : 0
    const pctRecaudo = metaPeriodo > 0 ? ((Number(form.recaudo_periodo) / metaPeriodo) * 100) : 0

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            const headers = await authHeaders()
            const enc = encodeURIComponent(plan.gestor_email)
            await axios.put(
                `${API}/comisiones/${empresaId}/datos/${periodo}/${enc}`,
                {
                    ...form,
                    ventas_periodo:   Number(form.ventas_periodo) || 0,
                    recaudo_periodo:  Number(form.recaudo_periodo) || 0,
                    nuevos_clientes:  Number(form.nuevos_clientes) || 0,
                    nuevos_comodatos: Number(form.nuevos_comodatos) || 0,
                },
                { headers }
            )
            onSave()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally { setSaving(false) }
    }

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalMedium} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>📊 Datos de Rendimiento — {plan?.gestor_nombre || plan?.gestor_email}</h3>
                    <button className={styles.modalClose} onClick={onClose}>✕</button>
                </div>
                <form onSubmit={handleSubmit} className={styles.modalBody}>
                    {/* Progreso en tiempo real */}
                    {metaPeriodo > 0 && (
                        <div className={styles.progressCard}>
                            <div className={styles.progressRow}>
                                <span>Ventas</span>
                                <strong>{pctVenta.toFixed(1)}% de meta</strong>
                            </div>
                            <div className={styles.progressTrack}>
                                <div className={styles.progressFillVenta}
                                    style={{ width: `${Math.min(pctVenta, 100)}%` }}></div>
                            </div>
                            <div className={styles.progressRow}>
                                <span>Recaudo</span>
                                <strong>{pctRecaudo.toFixed(1)}% de meta</strong>
                            </div>
                            <div className={styles.progressTrack}>
                                <div className={styles.progressFillRecaudo}
                                    style={{ width: `${Math.min(pctRecaudo, 100)}%` }}></div>
                            </div>
                            <p className={styles.hint}>Meta del período: ${metaPeriodo.toLocaleString('es-CO')}</p>
                        </div>
                    )}

                    <div className={styles.formGrid2}>
                        <div className={styles.formGroup}>
                            <label>Ventas del período (COP) *</label>
                            <input id="datos-ventas" type="number" min="0" step="1000" required
                                value={form.ventas_periodo}
                                onChange={e => setForm(f => ({ ...f, ventas_periodo: e.target.value }))}
                                placeholder="0" className={styles.input} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Recaudo del período (COP)</label>
                            <input type="number" min="0" step="1000"
                                value={form.recaudo_periodo}
                                onChange={e => setForm(f => ({ ...f, recaudo_periodo: e.target.value }))}
                                placeholder="0" className={styles.input} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Nuevos clientes</label>
                            <input type="number" min="0" value={form.nuevos_clientes}
                                onChange={e => setForm(f => ({ ...f, nuevos_clientes: e.target.value }))}
                                className={styles.inputSm} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Nuevos comodatos</label>
                            <input type="number" min="0" value={form.nuevos_comodatos}
                                onChange={e => setForm(f => ({ ...f, nuevos_comodatos: e.target.value }))}
                                className={styles.inputSm} />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label>Notas / observaciones</label>
                            <textarea value={form.notas}
                                onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
                                placeholder="Observaciones del período..." className={styles.textarea} rows={2} />
                        </div>
                        {isAdmin && (
                            <div className={styles.formGroup}>
                                <label>Estado</label>
                                <select value={form.estado} className={styles.input}
                                    onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                                    <option value="borrador">Borrador</option>
                                    <option value="enviado">Enviado</option>
                                    <option value="aprobado">Aprobado</option>
                                    <option value="rechazado">Rechazado</option>
                                </select>
                            </div>
                        )}
                    </div>

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btnGhost} onClick={onClose}>Cancelar</button>
                        {!isAdmin && (
                            <button type="submit" className={styles.btnSecondary} disabled={saving}
                                onClick={() => setForm(f => ({ ...f, estado: 'enviado' }))}>
                                {saving ? '...' : '📤 Enviar para aprobación'}
                            </button>
                        )}
                        <button type="submit" id="btn-guardar-datos" className={styles.btnPrimary} disabled={saving}>
                            {saving ? 'Guardando...' : '💾 Guardar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// ── Componente principal ───────────────────────────────────────────────────────
function DatosManager({ empresaId, periodo, planes, datos, onRefresh, setMsg, isAdmin, userEmail, soloPropio = false }) {
    const [modalDatos, setModalDatos] = useState(null)
    const [procesando, setProcesando] = useState(null)
    const [showImport, setShowImport] = useState(false)
    const fileRef = useRef()

    // Combinar planes con sus datos
    const gestoresConPlan = planes.map(plan => ({
        plan,
        datos: datos.find(d => d.gestor_email === plan.gestor_email) || null,
    }))

    const handleAprobar = async (plan) => {
        setProcesando(`aprobar-${plan.gestor_email}`)
        try {
            const headers = await authHeaders()
            const enc = encodeURIComponent(plan.gestor_email)
            const res = await axios.put(`${API}/comisiones/${empresaId}/datos/${periodo}/${enc}/aprobar`, {}, { headers })
            setMsg({ type: 'success', text: `✅ Aprobado. Comisión: $${(res.data.comision_calculada?.total_cop || 0).toLocaleString('es-CO')}` })
            onRefresh()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally { setProcesando(null) }
    }

    const handleRechazar = async (plan) => {
        const notas = window.prompt('Motivo del rechazo (opcional):') ?? ''
        setProcesando(`rechazar-${plan.gestor_email}`)
        try {
            const headers = await authHeaders()
            const enc = encodeURIComponent(plan.gestor_email)
            await axios.put(`${API}/comisiones/${empresaId}/datos/${periodo}/${enc}/rechazar`, { notas }, { headers })
            setMsg({ type: 'success', text: 'Datos rechazados. El gestor puede corregirlos.' })
            onRefresh()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally { setProcesando(null) }
    }

    // Importar desde Excel
    const handleImportExcel = (e) => {
        const files = [...(e.target.files || [])]
        if (files.length === 0) return

        const runImport = async () => {
            try {
                const workbooks = await Promise.all(files.map(readWorkbook))
                const headers = await authHeaders()
                let ok = 0, fail = 0

                const calculado = buildLiquidacionFromInsumos(workbooks, planes)
                const consolidado = calculado || parseConsolidadoQ1(workbooks[0], planes)

                if (consolidado) {
                    for (const row of consolidado) {
                        if (!row.plan) { fail++; continue }
                        try {
                            const enc = encodeURIComponent(row.plan.gestor_email)
                            await axios.put(`${API}/comisiones/${empresaId}/datos/${periodo}/${enc}`, row.payload, { headers })
                            ok++
                        } catch { fail++ }
                    }
                } else {
                    const ws  = workbooks[0].Sheets[workbooks[0].SheetNames[0]]
                    const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
                    for (const row of rows) {
                        const email = row['gestor_email'] || ''
                        if (!email) continue
                        // Verificar que existe un plan para ese gestor
                        const plan = planes.find(p => p.gestor_email === email)
                        if (!plan) { fail++; continue }
                        try {
                            const enc = encodeURIComponent(email)
                            await axios.put(`${API}/comisiones/${empresaId}/datos/${periodo}/${enc}`, {
                                ventas_periodo:   Number(row['ventas_periodo']) || 0,
                                recaudo_periodo:  Number(row['recaudo_periodo']) || 0,
                                nuevos_clientes:  Number(row['nuevos_clientes']) || 0,
                                nuevos_comodatos: Number(row['nuevos_comodatos']) || 0,
                                notas:            String(row['notas'] || ''),
                                estado:           'borrador',
                            }, { headers })
                            ok++
                        } catch { fail++ }
                    }
                }
                setMsg({ type: ok > 0 ? 'success' : 'error', text: `Importados: ${ok}, fallidos: ${fail}` })
                onRefresh()
            } catch (err) {
                setMsg({ type: 'error', text: 'Error procesando Excel: ' + err.message })
            } finally {
                e.target.value = ''
            }
        }
        runImport()
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>📊 Rendimiento — {periodo}</h2>
                    <p className={styles.sectionSub}>
                        {isAdmin ? 'Ingresa, revisa y aprueba los datos de cada gestor' : 'Registra tus datos de ventas y recaudo'}
                    </p>
                </div>
                {isAdmin && (
                    <div className={styles.headerBtns}>
                        <button className={styles.btnGhost} onClick={generarPlantillaExcel} title="Descargar plantilla Excel">
                            📥 Plantilla Excel
                        </button>
                        <button className={styles.btnSecondary} onClick={() => fileRef.current?.click()}>
                            📤 Importar insumos
                        </button>
                        <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple hidden onChange={handleImportExcel} />
                    </div>
                )}
            </div>

            {gestoresConPlan.length === 0 ? (
                <div className={styles.empty}>
                    <span>📊</span>
                    <p>No hay planes configurados para {periodo}</p>
                    {isAdmin && <small>Primero crea un plan en la pestaña "Planes / Reglas"</small>}
                </div>
            ) : (
                <div className={styles.datosGrid}>
                    {gestoresConPlan.map(({ plan, datos: d }) => {
                        const estado = d?.estado || 'sin_datos'
                        const cfg    = ESTADO_CONFIG[estado] || { label: 'Sin datos', color: '#4b5563', bg: 'rgba(75,85,99,0.15)' }
                        const metaPeriodo = Number(plan.metas?.periodo || plan.metas?.q1 || 0)
                        const pctV = metaPeriodo > 0 && d ? (d.ventas_periodo / metaPeriodo * 100) : 0
                        const pctR = metaPeriodo > 0 && d ? (d.recaudo_periodo / metaPeriodo * 100) : 0

                        return (
                            <div key={plan.gestor_email} className={styles.datosCard}>
                                {/* Header */}
                                <div className={styles.datosCardHeader}>
                                    <div className={styles.planAvatar}>
                                        {(plan.gestor_nombre || plan.gestor_email).charAt(0).toUpperCase()}
                                    </div>
                                    <div className={styles.datosCardInfo}>
                                        <h3>{plan.gestor_nombre || plan.gestor_email}</h3>
                                        <p className={styles.emailSmall}>{plan.gestor_email}</p>
                                    </div>
                                    <span className={styles.estadoBadge} style={{ color: cfg.color, background: cfg.bg }}>
                                        {cfg.label}
                                    </span>
                                </div>

                                {/* Datos si existen */}
                                {d ? (
                                    <div className={styles.datosBody}>
                                        <div className={styles.datosRow}>
                                            <span>Ventas</span>
                                            <strong>${d.ventas_periodo.toLocaleString('es-CO')}</strong>
                                        </div>
                                        {metaPeriodo > 0 && (
                                            <div className={styles.progressTrack} title={`${pctV.toFixed(1)}%`}>
                                                <div className={styles.progressFillVenta} style={{ width: `${Math.min(pctV, 100)}%` }}></div>
                                            </div>
                                        )}
                                        <div className={styles.datosRow}>
                                            <span>Recaudo</span>
                                            <strong>${d.recaudo_periodo.toLocaleString('es-CO')}</strong>
                                        </div>
                                        {metaPeriodo > 0 && (
                                            <div className={styles.progressTrack} title={`${pctR.toFixed(1)}%`}>
                                                <div className={styles.progressFillRecaudo} style={{ width: `${Math.min(pctR, 100)}%` }}></div>
                                            </div>
                                        )}
                                        {d.estado === 'aprobado' && d.comision_calculada?.total_cop > 0 && (
                                            <div className={styles.comisionTotal}>
                                                💰 Comisión: <strong>${d.comision_calculada.total_cop.toLocaleString('es-CO')}</strong>
                                            </div>
                                        )}
                                        {d.notas && <p className={styles.notasText}>💬 {d.notas}</p>}
                                    </div>
                                ) : (
                                    <div className={styles.datosEmpty}>Sin datos ingresados</div>
                                )}

                                {/* Acciones */}
                                <div className={styles.datosActions}>
                                    <button
                                        id={`btn-editar-datos-${plan.gestor_email}`}
                                        className={styles.btnSecondary}
                                        onClick={() => setModalDatos({ plan, datos: d })}
                                    >
                                        {d ? '✏️ Editar' : '➕ Ingresar datos'}
                                    </button>
                                    {isAdmin && d && d.estado === 'enviado' && (
                                        <>
                                            <button className={styles.btnSuccess}
                                                onClick={() => handleAprobar(plan)}
                                                disabled={!!procesando}>
                                                {procesando === `aprobar-${plan.gestor_email}` ? '...' : '✅ Aprobar'}
                                            </button>
                                            <button className={styles.btnDanger}
                                                onClick={() => handleRechazar(plan)}
                                                disabled={!!procesando}>
                                                {procesando === `rechazar-${plan.gestor_email}` ? '...' : '❌ Rechazar'}
                                            </button>
                                        </>
                                    )}
                                    {isAdmin && d && d.estado === 'borrador' && (
                                        <button className={styles.btnSuccess}
                                            onClick={() => handleAprobar(plan)}
                                            disabled={!!procesando}>
                                            {procesando === `aprobar-${plan.gestor_email}` ? '...' : '✅ Aprobar directo'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {modalDatos && (
                <DatosFormModal
                    empresaId={empresaId}
                    periodo={periodo}
                    plan={modalDatos.plan}
                    datos={modalDatos.datos}
                    onClose={() => setModalDatos(null)}
                    onSave={() => { setModalDatos(null); onRefresh(); setMsg({ type: 'success', text: '✅ Datos guardados' }) }}
                    setMsg={setMsg}
                    isAdmin={isAdmin}
                />
            )}
        </div>
    )
}

export default DatosManager
